import {EN_KO} from '../helpers/GlobalsHelper';

// map settings
EN_KO.settings.map = {};
EN_KO.settings.map.mapOpts = {
	disableDefaultUI: true,
	scrollwheel: false,
	zoomControl: true,
	zoom: 15
};

EN_KO.settings.map.locations = EN_KO.settings.map.locations || [];

class MapWrapper {
	constructor( canvasEl, mapOptions = {} ) {
		this.canvasEl = canvasEl;
		this.mapOptions = Object.assign( {}, EN_KO.settings.map.mapOpts, mapOptions );

		this.markers = [];

		let markerEls = Array.from( this.canvasEl.querySelectorAll( '.marker' ) );
		if ( markerEls.length <= 0 ) {
			markerEls = EN_KO.settings.map.locations;
		}

		this.initMap();
		this.setMarkers( markerEls );
		this.fitBounds();

		// Dispatch Event.
		this.canvasEl.dispatchEvent( new CustomEvent( 'map_initialized.koe', { bubbles: true, detail: this } ) );
	}

	getMap() {
		return this.googleMap;
	}

	initMap() {
		const google = window.google;
		const mapOptions = this.mapOptions;

		//create Map inside the canvasEl.
		this.googleMap = new google.maps.Map(
			this.canvasEl,
			{
				mapId: this.canvasEl.dataset.mapId || 'fc192e1a8ada5b94',
				...mapOptions
			}
		);

		if ( typeof mapOptions.center !== 'undefined' ) {
			window.addEventListener( 'resize', () => {
				this.googleMap.setCenter( mapOptions.center );
			} );
		}

		// Initialize.
		this.infoWindow = new google.maps.InfoWindow();
		this.canvasEl.classList.add( 'is-initialized' );

		return this.googleMap;
	}

	setMarkers( markerEls ) {
		this.clearMarkers();
		const locations = this.getLocations( markerEls );
		this.markers = locations.map( ( location ) => this.getMarker( location ) );
		return this.markers;
	}

	clearMarkers() {
		this.markers.forEach( marker => marker.setMap( null ) );
		this.markers = [];
	}

	getLocations( markerEls ) {
		return markerEls.map( ( markerEl ) => {
			if ( markerEl instanceof HTMLElement ) {

				const iconEl = markerEl.querySelector( '.icon' );
				const popupEl = markerEl.querySelector( '.popup' );

				return Object.assign(
					{},
					{
						'title': markerEl.dataset.title,
						'lat': parseFloat( markerEl.dataset.lat ),
						'lng': parseFloat( markerEl.dataset.lng ),
						'icon': iconEl || this.getDefaultPin( markerEl.dataset.type ),
						'zIndex': parseInt( markerEl.dataset.zIndex ) || 20,
					},
					popupEl ? {'popup': popupEl.innerHTML} : {'href': markerEl.href},
				);
			} else {
				const location = markerEl;
				const action = location[5] ?? '';

				return Object.assign(
					{},
					{
						'title': location[0],
						'lat': parseFloat( location[1] ),
						'lng': parseFloat( location[2] ),
						'icon': this.getDefaultPin( location[3] ),
						'zIndex': parseInt( location[4] ) || 20,
					},
					action.indexOf( 'http' ) !== 0 ? { 'popup': action } : { 'href': action },
				);
			}
		} );
	}

	getMarker( location ) {
		const map = this.getMap();
		const google = window.google;
		const {
			title,
			lat,
			lng,
			icon: iconEl,
			zIndex,
			href,
			popup
		} = location;

		const position = new google.maps.LatLng( lat, lng );
		const marker = new google.maps.marker.AdvancedMarkerElement( {
			map,
			position,
			content: iconEl,
			title,
			zIndex,
		} );
		// Save the args this marker was created with.
		marker._args = location;

		google.maps.event.addListener( marker, 'click', () => {

			if ( href && href.indexOf( 'http' ) === 0 ) {
				const url = new URL( href );
				if ( url.host !== location.host ) {
					window.open( href, '_blank' );
				} else {
					window.location.href = href;
				}

			} else {
				this.infoWindow.setContent( popup );
				this.infoWindow.open( map, marker );
			}
		} );

		return marker;
	}

	getDefaultPin( type = 'koe' ) {
		let src = EN_KO.settings.baseUrl + 'images/pin';

		if ( type ) {
			src += '_' + type;
		}

		const isRetina = window.matchMedia && window.matchMedia( "(-webkit-min-device-pixel-ratio: 1.25), (min-resolution: 120dpi)" ).matches;
		if ( isRetina ) {
			src += '@2x';
		}
		src += '.png';

		const defaultPinEl = document.createElement( 'img' );
		defaultPinEl.src = src;
		defaultPinEl.width = 36;
		defaultPinEl.height = 48;
		defaultPinEl.style.width = '36px';
		defaultPinEl.style.height = '48px';

		return defaultPinEl;
	}

	fitBounds() {
		const map = this.getMap();
		const google = window.google;

		// If there are two or more locations, we need to fit the map to them.
		if ( this.markers.length > 1 ) {

			// Get Center of all Markers and fit the map to it.
			const bounds = this.markers.reduce(
				( bounds, marker ) => {
					bounds.extend( marker.position );
					return bounds;
				},
				new google.maps.LatLngBounds( map.getCenter(), map.getCenter() )
			);
			map.fitBounds( bounds );
		} else if ( this.markers.length === 1 ) {
			const marker = this.markers[0];
			marker.map.setCenter( marker.position );
		}
	}
}


const initMap = function( canvasEl ) {
	canvasEl.SL_GOOGLE_MAPS ??= new MapWrapper( canvasEl, canvasEl.dataset.mapOptions ? JSON.parse( canvasEl.dataset.mapOptions ) : {} );
	return canvasEl.SL_GOOGLE_MAPS;
};

const initMaps = function( scopeEl = document ) {

	EN_KO.functions.onInitMaps().then( async( [google] ) => {
		await google.maps.importLibrary( 'marker' );

		const canvasEls = scopeEl.querySelectorAll( '.map_canvas' );
		canvasEls.forEach( ( canvasEl ) => initMap( canvasEl ) );
	} );
};

window.addEventListener( 'load_embed.koe', ( ev ) => initMaps( ev.target ) );

export {
	MapWrapper as default,
	initMap,
	initMaps,
};
